import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MapPin, Trash2, Users, Locate, Edit } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AttendanceLocations() {
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [assignDialog, setAssignDialog] = useState({ open: false, location: null });
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({
    name: "",
    type: "office",
    address: "",
    latitude: "",
    longitude: "",
    radius_meters: 100,
    notes: "",
    employee_ids: [],
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [locRes, empRes] = await Promise.all([
        axios.get(`${API}/hr/attendance-locations?include_inactive=false`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/hr/employees`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLocations(locRes.data || []);
      setEmployees(empRes.data || []);
    } catch (e) {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      type: "office",
      address: "",
      latitude: "",
      longitude: "",
      radius_meters: 100,
      notes: "",
      employee_ids: [],
    });
    setEditingId(null);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    toast.info("Fetching current location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }));
        toast.success(`Location captured (accuracy ${Math.round(pos.coords.accuracy)} m)`);
      },
      (err) => toast.error(`Location error: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        type: form.type,
        address: form.address || null,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius_meters: parseInt(form.radius_meters, 10),
        notes: form.notes || null,
        employee_ids: form.type === "project_site" ? form.employee_ids : [],
      };
      if (editingId) {
        await axios.put(`${API}/hr/attendance-locations/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/hr/attendance-locations`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Location created");
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save location");
    }
  };

  const handleEdit = (loc) => {
    setEditingId(loc.id);
    setForm({
      name: loc.name,
      type: loc.type,
      address: loc.address || "",
      latitude: loc.latitude,
      longitude: loc.longitude,
      radius_meters: loc.radius_meters,
      notes: loc.notes || "",
      employee_ids: [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this location? Existing attendance records are not affected.")) return;
    try {
      await axios.delete(`${API}/hr/attendance-locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Location deactivated");
      fetchAll();
    } catch (e) {
      toast.error("Failed to deactivate location");
    }
  };

  const openAssignments = async (loc) => {
    setAssignDialog({ open: true, location: loc });
    try {
      const res = await axios.get(`${API}/hr/attendance-locations/${loc.id}/assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignments(res.data || []);
    } catch (e) {
      toast.error("Failed to load assignments");
    }
  };

  const addAssignment = async (employee_id) => {
    try {
      await axios.post(
        `${API}/hr/attendance-locations/${assignDialog.location.id}/assignments`,
        { employee_ids: [employee_id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Employee assigned");
      openAssignments(assignDialog.location);
    } catch (e) {
      toast.error("Failed to assign employee");
    }
  };

  const removeAssignment = async (employee_id) => {
    try {
      await axios.delete(
        `${API}/hr/attendance-locations/${assignDialog.location.id}/assignments/${employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Assignment removed");
      openAssignments(assignDialog.location);
    } catch (e) {
      toast.error("Failed to remove assignment");
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-500 dark:text-slate-400">Loading…</div>;
  }

  const assignedEmpIds = new Set(assignments.map((a) => a.employee_id));
  const unassignedEmployees = employees.filter((e) => !assignedEmpIds.has(e.id));

  return (
    <div className="space-y-6" data-testid="attendance-locations-tab">
      <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Attendance Locations</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Office locations apply to everyone. Site locations require per-employee assignment.
          </p>
        </div>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="bg-brillien-600 hover:bg-brillien-700 text-white"
              data-testid="add-location-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Location
            </Button>
          </DialogTrigger>
          <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Location" : "Add Attendance Location"}</DialogTitle>
              <DialogDescription>
                Define the geofence by entering coordinates and a radius. Office locations apply to all employees; site locations need per-employee assignment.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="location-name-input"
                  />
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="location-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="office">Office (All Employees)</SelectItem>
                      <SelectItem value="project_site">Project Site (Per-Employee)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Latitude *</Label>
                  <Input
                    type="number"
                    step="0.000001"
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                    required
                    data-testid="location-lat-input"
                  />
                </div>
                <div>
                  <Label>Longitude *</Label>
                  <Input
                    type="number"
                    step="0.000001"
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                    required
                    data-testid="location-lng-input"
                  />
                </div>
                <div>
                  <Label>Radius (m) *</Label>
                  <Input
                    type="number"
                    min="10"
                    max="5000"
                    value={form.radius_meters}
                    onChange={(e) => setForm({ ...form, radius_meters: e.target.value })}
                    required
                    data-testid="location-radius-input"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={useCurrentLocation}
                data-testid="use-current-location-btn"
              >
                <Locate className="h-3 w-3 mr-2" /> Use my current location
              </Button>

              {form.type === "project_site" && !editingId && (
                <div>
                  <Label>Pre-assign Employees</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 mt-1 dark:border-slate-700">
                    {employees.map((emp) => (
                      <label key={emp.id} className="flex items-center gap-2 p-1 text-sm">
                        <input
                          type="checkbox"
                          checked={form.employee_ids.includes(emp.id)}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...form.employee_ids, emp.id]
                              : form.employee_ids.filter((id) => id !== emp.id);
                            setForm({ ...form, employee_ids: ids });
                          }}
                        />
                        <span className="dark:text-slate-200">{emp.name} ({emp.email})</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brillien-600 hover:bg-brillien-700"
                  data-testid="save-location-btn"
                >
                  {editingId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </Card>

      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Coordinates</TableHead>
              <TableHead>Radius</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 dark:text-slate-400 py-8">
                  No locations yet. Click "Add Location" to create one.
                </TableCell>
              </TableRow>
            )}
            {locations.map((loc) => (
              <TableRow key={loc.id} data-testid={`location-row-${loc.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-brillien-600" />
                    <span className="font-medium dark:text-white">{loc.name}</span>
                  </div>
                  {loc.address && <p className="text-xs text-slate-500 mt-1">{loc.address}</p>}
                </TableCell>
                <TableCell>
                  <Badge variant={loc.type === "office" ? "default" : "secondary"}>
                    {loc.type === "office" ? "Office" : "Site"}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
                </TableCell>
                <TableCell>{loc.radius_meters} m</TableCell>
                <TableCell>
                  {loc.applies_to_all ? (
                    <Badge className="bg-emerald-100 text-emerald-700">All Employees</Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openAssignments(loc)}
                      data-testid={`manage-assignments-${loc.id}`}
                    >
                      <Users className="h-3 w-3 mr-1" /> Manage
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(loc)}
                    data-testid={`edit-location-${loc.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(loc.id)}
                    data-testid={`delete-location-${loc.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Assignments dialog */}
      <Dialog
        open={assignDialog.open}
        onOpenChange={(open) => setAssignDialog({ open, location: open ? assignDialog.location : null })}
      >
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Manage Assignments — {assignDialog.location?.name}
            </DialogTitle>
            <DialogDescription>
              Add or remove employees who are allowed to check in / out at this site location.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold dark:text-slate-200 mb-2">Currently Assigned ({assignments.length})</h4>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 dark:border-slate-700">
                {assignments.length === 0 && (
                  <p className="text-xs text-slate-500 p-2">No employees assigned yet.</p>
                )}
                {assignments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded"
                  >
                    <span className="text-sm dark:text-slate-200">
                      {a.employee_name || a.employee_id}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAssignment(a.employee_id)}
                      data-testid={`unassign-${a.employee_id}`}
                    >
                      <Trash2 className="h-3 w-3 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold dark:text-slate-200 mb-2">
                Available Employees ({unassignedEmployees.length})
              </h4>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 dark:border-slate-700">
                {unassignedEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded"
                  >
                    <span className="text-sm dark:text-slate-200">{emp.name} ({emp.email})</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addAssignment(emp.id)}
                      data-testid={`assign-${emp.id}`}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Assign
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
